export interface ParallelRuntimeAdmissionDecision {
  schemaVersion: 1;
  contractVersion: "browser-parallel-runtime-admission-v1";
  allowed: boolean;
  fallbackProfile: "serial-production-v1" | null;
  reason: string | null;
  logicalCpuCount: number | null;
  deviceMemoryGb: number | null;
  heapUsageRatio: number | null;
  memoryPressure: boolean;
  injectedPressure: boolean;
}

type NavigatorMemory = Navigator & { deviceMemory?: number };
type PerformanceMemory = Performance & { memory?: { usedJSHeapSize: number; jsHeapSizeLimit: number } };

const CONTRACT_VERSION = "browser-parallel-runtime-admission-v1" as const;

export function evaluateParallelRuntimeAdmission(options?: { forceMemoryPressure?: boolean }): ParallelRuntimeAdmissionDecision {
  const logicalCpuCount = typeof navigator !== "undefined" && Number.isFinite(navigator.hardwareConcurrency)
    ? navigator.hardwareConcurrency
    : null;
  const deviceMemoryGb = typeof navigator !== "undefined"
    ? ((navigator as NavigatorMemory).deviceMemory ?? null)
    : null;
  const memory = typeof performance !== "undefined" ? (performance as PerformanceMemory).memory : undefined;
  const heapUsageRatio = memory && memory.jsHeapSizeLimit > 0
    ? memory.usedJSHeapSize / memory.jsHeapSizeLimit
    : null;
  const injectedPressure = options?.forceMemoryPressure === true;
  const memoryPressure = injectedPressure
    || (heapUsageRatio !== null && heapUsageRatio >= 0.82)
    || (deviceMemoryGb !== null && deviceMemoryGb <= 2);

  let reason: string | null = null;
  if (typeof Worker === "undefined") reason = "WORKER_API_UNAVAILABLE";
  else if (typeof document !== "undefined" && document.visibilityState !== "visible") reason = "DOCUMENT_NOT_VISIBLE";
  else if (memoryPressure) reason = injectedPressure ? "MEMORY_PRESSURE_INJECTED" : "MEMORY_PRESSURE";
  else if (logicalCpuCount !== null && logicalCpuCount < 2) reason = "INSUFFICIENT_LOGICAL_PARALLELISM";

  return {
    schemaVersion: 1,
    contractVersion: CONTRACT_VERSION,
    allowed: reason === null,
    fallbackProfile: reason === null ? null : "serial-production-v1",
    reason,
    logicalCpuCount,
    deviceMemoryGb,
    heapUsageRatio,
    memoryPressure,
    injectedPressure,
  };
}
